import React, { useState } from 'react';
import Breadcrumbs from '@components/Breadcrumbs';
import { Button, Heading, Alert } from '@puppet/react-components';
import PageLayout from '@components/PageLayout';
import { BreadcrumbsType } from '@components/Breadcrumbs/Breadcrumbs';
import useWorkspaceName from '@hooks/useWorkspaceName';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { LINKS } from 'src/routes';
import useWorkspaceDomain from '@hooks/useWorkspaceDomain';
import { useListModulesV1, useDeleteModuleV1 } from '@services/cd4pe/modules';
import ListCodeProjects from '@codeDelivery/components/ListCodeProjects';
import useDebounce from '@hooks/useDebounce';

const ListModules = () => {
  const { t } = useTranslation('codeDelivery');
  const navigate = useNavigate();
  const workspaceName = useWorkspaceName();
  const [prefix, setPrefix] = React.useState('');
  const workspaceId = useWorkspaceDomain();
  const modules = useListModulesV1({
    workspaceId,
    prefix: useDebounce(prefix),
  });
  const deleteModule = useDeleteModuleV1();
  const location = useLocation();
  const [successAlert, setSuccessAlert] = useState(
    !!location.state?.alertMessage,
  );

  const onClickEdit = (projectSrcRepoName: string) => {
    return navigate(
      LINKS.codeDelivery.editModule({
        path: { workspace: workspaceName, name: projectSrcRepoName },
      }),
    );
  };

  const breadcrumbs: BreadcrumbsType = [
    {
      displayName: workspaceName,
    },
    {
      displayName: t('listModules.breadcrumb.modules'),
    },
  ];

  const handleAlertClose = () => {
    setSuccessAlert(false);
    const header = document.querySelector('.listModules__header');
    if (header) {
      header.classList.remove('with-alert');
    }
  };

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageLayout.Breadcrumbs>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          {successAlert && location.state?.alertMessage && (
            <div className="listModules__alert">
              <Alert type="success" closeable onClose={handleAlertClose}>
                {location.state.alertMessage}
              </Alert>
            </div>
          )}
        </PageLayout.Breadcrumbs>
        <div
          className={`listModules__header ${successAlert ? 'with-alert' : ''}`}
        >
          <Heading>{t('listModules.header.title')}</Heading>
          <Button
            data-testid="list-modules-add-new-button"
            onClick={() =>
              navigate(
                LINKS.codeDelivery.newModule({
                  path: { workspace: workspaceName },
                }),
              )
            }
          >
            {t('listModules.header.newModule')}
          </Button>
        </div>
      </PageLayout.Header>
      <PageLayout.Content>
        <ListCodeProjects
          translationSuffix="module"
          projectsByPage={
            modules.data?.pages?.map((page) => page.modules) ?? []
          }
          infiniteQueryDetails={modules}
          getProjectLink={(name) =>
            LINKS.codeDelivery.viewModule({
              path: { workspace: workspaceName, name },
            })
          }
          onDelete={(name, onSuccess: () => void) => {
            deleteModule.mutate(
              {
                workspaceId,
                moduleName: name,
              },
              {
                onSuccess,
              },
            );
          }}
          onEdit={onClickEdit}
          deleteError={deleteModule.error}
          prefix={prefix}
          setPrefix={setPrefix}
        />
      </PageLayout.Content>
    </PageLayout>
  );
};

export default ListModules;
